/**
 * Snap a fixture to the nearest valid position when a collision is detected.
 * Pure computation — no Firebase calls.
 *
 * Strategy: find what we're colliding with, calculate the edge of that
 * obstacle, and place our object flush against it.
 */

const {
  getObjectCorners,
  getObjectClearanceCorners,
  getObjectClearanceFootprints,
  projectPolygon,
  doPolygonsOverlap,
} = require('./geometry');
const { getClearanceConfig } = require('./clearanceConfig');
const { wallOutlineEnds } = require('../geometry/wallOutlineEnds');
const { checkPlacement } = require('./checkPlacement');
const {
  computeDirectionalMTV,
} = require('../bathroom-analyzer/pipeline/5.1-physicalCollisions');
const { wallToBoundaryObb } = require('./physicalBoundary');

// Push exactly to the obstacle edge — every collision-detection path runs
// through doPolygonsOverlap / overlapAmount with COLLISION_TOLERANCE
// (~3.2mm) baked in, so an extra geometric gap here would just stack on
// top of that tolerance and let the snap overshoot into adjacent fixtures.
const SNAP_GAP = 0;
const MAX_SNAP_DISTANCE = 0.3; // Don't snap further than 30cm

/**
 * Constrain a snapped center to preserve the original perpendicular distance from a wall.
 * Only changes the lateral component; keeps the original wall-normal offset.
 */
function preserveWallDistance(snappedCenter, originalCenter, wallDir) {
  const wallNormal = { x: -wallDir.z, z: wallDir.x };

  // Original perpendicular distance
  const origPerp =
    originalCenter.x * wallNormal.x + originalCenter.z * wallNormal.z;
  // Snapped perpendicular distance
  const snapPerp =
    snappedCenter.x * wallNormal.x + snappedCenter.z * wallNormal.z;

  const perpShift = origPerp - snapPerp;
  if (Math.abs(perpShift) < 0.001) return snappedCenter;

  return {
    x: snappedCenter.x + perpShift * wallNormal.x,
    y: snappedCenter.y,
    z: snappedCenter.z + perpShift * wallNormal.z,
  };
}

/**
 * Get wall direction vector from a wall object.
 * Tries multiple data sources in order:
 * 1. start/end (set by getWallEndpoints or findClosestWall)
 * 2. polygonCorners (bottom two corners by Y, then sorted by X/Z)
 * 3. alignment.bottomLeft/bottomRight (from structure processing)
 * 4. center/dimensions/rotation (computed endpoints)
 *
 * @returns {{ wallDir: {x,z}, wallStart: {x,z}, wallLength: number } | null}
 */
function getWallAxis(wall) {
  let start, end;
  // A structure wall's outline has a corner at every junction and a room
  // wall's can step, so its ends are not "the two lowest corners".
  const outlineEnds = wallOutlineEnds(wall.polygonCorners);

  if (wall.start && wall.end) {
    start = { x: wall.start.x, z: wall.start.z };
    end = { x: wall.end.x, z: wall.end.z };
  } else if (outlineEnds) {
    const ends = [...outlineEnds];
    ends.sort((a, b) => {
      if (Math.abs(a.x - b.x) > 0.001) return a.x - b.x;
      return a.z - b.z;
    });
    start = { x: ends[0].x, z: ends[0].z };
    end = { x: ends[1].x, z: ends[1].z };
  } else if (wall.alignment?.bottomLeft && wall.alignment?.bottomRight) {
    start = wall.alignment.bottomLeft;
    end = wall.alignment.bottomRight;
  } else if (wall.center && wall.dimensions) {
    const halfLength = (wall.dimensions.x || 1) / 2;
    const rotY = wall.rotation?.y || 0;
    const cos = Math.cos(rotY);
    const sin = Math.sin(rotY);
    start = {
      x: wall.center.x - cos * halfLength,
      z: wall.center.z + sin * halfLength,
    };
    end = {
      x: wall.center.x + cos * halfLength,
      z: wall.center.z - sin * halfLength,
    };
  }

  if (!start || !end) return null;

  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const length = Math.sqrt(dx * dx + dz * dz);
  if (length === 0) return null;

  return {
    wallDir: { x: dx / length, z: dz / length },
    wallStart: start,
    wallLength: length,
  };
}

/**
 * Project an object's XZ extent onto a direction axis.
 * Returns { min, max } scalar range along the axis.
 */
function projectObjectOntoAxis(obj, axis) {
  const corners = getObjectCorners(obj);
  return projectPolygon(corners, axis);
}

/**
 * Try to snap a colliding object to a valid position.
 *
 * @param {Object} object - The object at its proposed (invalid) position
 * @param {Object} roomData - Room data { walls, doors, windows, openings, objects, floors }
 * @param {Object} collisionResult - Result from checkPlacement ({ collisions })
 * @param {Object} [options]
 * @param {string[]} [options.excludeIds]
 * @param {string[]} [options.excludeGroupIds]
 * @param {Object} [options.snappedWall] - Wall the object is currently snapped to (overrides parentWallIds lookup)
 * @returns {{ snapped: boolean, center: {x,y,z}, snapDistance: number } | null}
 */
function snapToValidPosition(object, roomData, collisionResult, options = {}) {
  const debugLog = [];
  const result = snapToValidPositionInner(
    object,
    roomData,
    collisionResult,
    options,
    debugLog
  );
  if (result) result.debugLog = debugLog;
  else if (debugLog.length > 0) {
    return { snapped: false, debugLog };
  }

  // For wall-attached objects, ensure snap only moves along wall (not perpendicular)
  const wall =
    options.snappedWall ||
    (object.parentWallIds?.[0]
      ? (roomData.walls || []).find((w) => w.id === object.parentWallIds[0])
      : null);
  if (result?.snapped && wall) {
    const wallInfo = getWallAxis(wall);
    if (wallInfo) {
      result.center = preserveWallDistance(
        result.center,
        object.center,
        wallInfo.wallDir
      );
    }
  }

  return result;
}

function snapToValidPositionInner(
  object,
  roomData,
  collisionResult,
  options = {},
  debugLog = []
) {
  if (!collisionResult.collisions?.length) return null;

  // Find the wall for wall-relative snapping — prefer snappedWall, fall back to parentWallIds
  // NOTE: despite the name "parentWall", this is the snapped wall when available
  const parentWall =
    options.snappedWall ||
    (object.parentWallIds?.[0]
      ? (roomData.walls || []).find((w) => w.id === object.parentWallIds[0])
      : null);

  // --- Handle wall-bounds collisions (clamp to wall endpoints) ---
  const wallCollisions = collisionResult.collisions.filter(
    (c) => c.type === 'parent-wall-bounds'
  );
  if (wallCollisions.length > 0 && parentWall) {
    const wallInfo = getWallAxis(parentWall);
    if (wallInfo) {
      const { wallDir, wallStart, wallLength } = wallInfo;
      const objCorners = getObjectCorners(object);
      const objProj = projectPolygon(objCorners, wallDir);
      const wallStartProj = wallStart.x * wallDir.x + wallStart.z * wallDir.z;
      const wallEndProj = wallStartProj + wallLength;
      const overshootLeft = wallStartProj - objProj.min;
      const overshootRight = objProj.max - wallEndProj;
      let shift = 0;
      if (overshootLeft > 0) shift = overshootLeft;
      else if (overshootRight > 0) shift = -overshootRight;
      if (Math.abs(shift) > 0 && Math.abs(shift) <= MAX_SNAP_DISTANCE) {
        const snappedCenter = {
          x: object.center.x + shift * wallDir.x,
          y: object.center.y,
          z: object.center.z + shift * wallDir.z,
        };
        const validation = checkPlacement(
          { ...object, center: snappedCenter },
          roomData,
          options
        );
        if (validation.valid) {
          debugLog.push(
            `wall-bounds: shift ${(shift * 1000).toFixed(0)}mm → valid`
          );
          return {
            snapped: true,
            center: snappedCenter,
            snapDistance: Math.abs(shift),
          };
        }
        debugLog.push(
          `wall-bounds: shift ${(shift * 1000).toFixed(0)}mm → still invalid (${validation.collisions?.[0]?.type})`
        );
      } else if (Math.abs(shift) > MAX_SNAP_DISTANCE) {
        debugLog.push(
          `wall-bounds: need ${(Math.abs(shift) * 1000).toFixed(0)}mm > max ${(MAX_SNAP_DISTANCE * 1000).toFixed(0)}mm`
        );
      }
    }
  }

  // --- Handle door-swing collisions (push along wall to clear door zone) ---
  const doorCollisions = collisionResult.collisions.filter(
    (c) => c.type === 'door-swing'
  );
  if (doorCollisions.length > 0 && parentWall) {
    const wallInfo = getWallAxis(parentWall);
    if (wallInfo) {
      const { wallDir } = wallInfo;
      const objCorners = getObjectCorners(object);

      for (const collision of doorCollisions) {
        const door = (roomData.doors || []).find(
          (d) => d.id === collision.target.id
        );
        if (!door?.center) continue;

        const { calculateDoorZone } = require('./doorCollision');
        const doorZones = calculateDoorZone(door, roomData.walls || []);
        if (!doorZones) continue;

        // Use directional SAT to find exact push distance in each direction
        const leftDir = { x: -wallDir.x, z: -wallDir.z };
        const rightDir = { x: wallDir.x, z: wallDir.z };

        // Compute push needed against all door zone sub-polygons
        let leftPush = 0;
        let rightPush = 0;
        for (const zone of doorZones) {
          const lt = computeDirectionalMTV(objCorners, zone, leftDir);
          if (lt > leftPush) leftPush = lt;
          const rt = computeDirectionalMTV(objCorners, zone, rightDir);
          if (rt > rightPush) rightPush = rt;
        }

        // Try both directions, pick the closer valid one
        const candidates = [];
        if (leftPush > 0)
          candidates.push({ shift: leftPush + SNAP_GAP, dir: leftDir });
        if (rightPush > 0)
          candidates.push({ shift: rightPush + SNAP_GAP, dir: rightDir });
        candidates.sort((a, b) => a.shift - b.shift);

        let bestDoorSnap = null;
        for (const { shift, dir } of candidates) {
          if (shift > MAX_SNAP_DISTANCE) {
            debugLog.push(`door-swing: ${(shift * 1000).toFixed(0)}mm > max`);
            continue;
          }

          const snappedCenter = {
            x: object.center.x + shift * dir.x,
            y: object.center.y,
            z: object.center.z + shift * dir.z,
          };
          const validation = checkPlacement(
            { ...object, center: snappedCenter },
            roomData,
            options
          );
          if (validation.valid) {
            bestDoorSnap = {
              snapped: true,
              center: snappedCenter,
              snapDistance: shift,
            };
            break;
          } else {
            debugLog.push(
              `door-swing: ${(shift * 1000).toFixed(0)}mm → still invalid (${validation.collisions?.[0]?.type})`
            );
          }
        }
        if (bestDoorSnap) {
          debugLog.push(
            `door-swing: snap ${(bestDoorSnap.snapDistance * 1000).toFixed(0)}mm → valid`
          );
          return bestDoorSnap;
        }
      }
    }
  }

  // --- Handle physical-boundary collisions (slide along parent wall to clear blocking walls) ---
  // Each physical-boundary collision now identifies a SPECIFIC wall the
  // fixture body overlaps. We compute MTV against each blocking wall's OBB
  // along ±parentWall.wallDir and pick the smallest combined slide that
  // clears all of them and revalidates. This replaces the earlier polygon-
  // based shift logic which guessed the binding edge by Euclidean distance
  // and failed for non-convex rooms.
  const physBoundaryCollisions = collisionResult.collisions.filter(
    (c) => c.type === 'physical-boundary'
  );
  if (physBoundaryCollisions.length > 0 && !parentWall) {
    debugLog.push(`physical-boundary: no parent wall to slide along`);
  }
  if (physBoundaryCollisions.length > 0 && parentWall) {
    const wallInfo = getWallAxis(parentWall);
    if (wallInfo) {
      const { wallDir } = wallInfo;
      const objCorners = getObjectCorners(object);

      // Collect the OBBs of every blocking wall (each collision carries its
      // wall reference). De-duplicate by id in case multiple corners landed
      // in the same wall.
      const blockingObbs = [];
      const seenIds = new Set();
      for (const collision of physBoundaryCollisions) {
        const wall = collision.target?.wall;
        const wallId = collision.target?.id;
        if (!wall || seenIds.has(wallId)) continue;
        seenIds.add(wallId);
        const obb = wallToBoundaryObb(wall);
        if (obb) blockingObbs.push({ wallId, obb });
      }

      if (blockingObbs.length === 0) {
        debugLog.push(
          `physical-boundary: no resolvable wall references in collisions`
        );
      } else {
        // Per direction, the binding shift across all blocking walls is the
        // max MTV — we need to clear ALL of them, not just one.
        const candidates = [];
        for (const sign of [+1, -1]) {
          const dir = { x: sign * wallDir.x, z: sign * wallDir.z };
          const label = sign > 0 ? '+wallDir' : '-wallDir';
          let combined = 0;
          let perWallLog = [];
          for (const { wallId, obb } of blockingObbs) {
            const mtv = computeDirectionalMTV(objCorners, obb, dir);
            perWallLog.push(`${wallId.slice(-6)}=${(mtv * 1000).toFixed(0)}mm`);
            if (mtv > combined) combined = mtv;
          }
          debugLog.push(
            `pb ${label}: ${perWallLog.join(', ')} → combined=${(combined * 1000).toFixed(0)}mm`
          );
          if (combined > 0) {
            candidates.push({
              dir,
              label,
              shift: combined + SNAP_GAP,
            });
          }
        }
        candidates.sort((a, b) => a.shift - b.shift);

        let resolved = false;
        for (const { dir, label, shift } of candidates) {
          if (shift > MAX_SNAP_DISTANCE) {
            debugLog.push(
              `pb ${label}: ${(shift * 1000).toFixed(0)}mm > max ${(MAX_SNAP_DISTANCE * 1000).toFixed(0)}mm`
            );
            continue;
          }
          const snappedCenter = {
            x: object.center.x + shift * dir.x,
            y: object.center.y,
            z: object.center.z + shift * dir.z,
          };
          const validation = checkPlacement(
            { ...object, center: snappedCenter },
            roomData,
            options
          );
          if (validation.valid) {
            debugLog.push(
              `physical-boundary: shift ${(shift * 1000).toFixed(0)}mm ${label} → valid`
            );
            return {
              snapped: true,
              center: snappedCenter,
              snapDistance: shift,
            };
          }
          debugLog.push(
            `pb ${label}: ${(shift * 1000).toFixed(0)}mm → still invalid (${validation.collisions?.[0]?.type})`
          );
        }
        if (!resolved && candidates.length === 0) {
          debugLog.push(
            `physical-boundary: no positive MTV in either direction — body may exit perpendicular to parent wall`
          );
        }
      }
    }
  }

  // --- Handle clearance-boundary collisions ---
  // Each clearance-boundary collision identifies a specific wall the clearance
  // zone overlaps. Resolution strategy:
  //   1. For tub/shower fixtures with adjustable clearance offset: try sliding
  //      the clearance zone along the body axis first (no body movement, cheap).
  //   2. If slide is unavailable or insufficient: push the whole fixture along
  //      ±parentWallDir using per-wall MTV, same pattern as physical-boundary.
  const clearBoundaryCollisions = collisionResult.collisions.filter(
    (c) => c.type === 'clearance-boundary'
  );
  if (clearBoundaryCollisions.length > 0 && !parentWall) {
    debugLog.push(`clearance-boundary: no parent wall to slide along`);
  }
  if (clearBoundaryCollisions.length > 0 && parentWall) {
    const wallInfo = getWallAxis(parentWall);
    if (wallInfo) {
      const { wallDir } = wallInfo;

      // Resolve OBBs of every blocking wall (de-duped by id).
      const blockingObbs = [];
      const seenIds = new Set();
      for (const collision of clearBoundaryCollisions) {
        const wall = collision.target?.wall;
        const wallId = collision.target?.id;
        if (!wall || seenIds.has(wallId)) continue;
        seenIds.add(wallId);
        const obb = wallToBoundaryObb(wall);
        if (obb) blockingObbs.push({ wallId, obb });
      }

      if (blockingObbs.length === 0) {
        debugLog.push(`clearance-boundary: no resolvable wall references`);
      } else {
        // Step 1: try clearance-offset slide for tub/shower fixtures.
        // The clearance polygon can shift laterally inside the body's
        // footprint without moving the body. For each direction, the binding
        // slide across all blocking walls is max(MTV); we pick the smallest
        // valid resulting offset that stays within the slide range.
        const SLIDEABLE_TYPES = new Set(['bathtub', 'shower']);
        const objType = (object.type || '').toLowerCase();
        if (SLIDEABLE_TYPES.has(objType)) {
          const objConfig = getClearanceConfig(object);
          const clearanceW = objConfig.clearanceWidth || object.dimensions.x;
          const physicalW = object.dimensions.x;
          const slideRange = (physicalW - clearanceW) / 2;

          if (slideRange > 0.01) {
            const currentClearance = getObjectClearanceCorners(
              object,
              objConfig,
              object.clearanceOffset || 0
            );
            const currentOffset = object.clearanceOffset || 0;
            const slideCandidates = [];
            for (const sign of [+1, -1]) {
              const dir = { x: sign * wallDir.x, z: sign * wallDir.z };
              const label = sign > 0 ? '+wallDir' : '-wallDir';
              let combined = 0;
              for (const { obb } of blockingObbs) {
                const mtv = computeDirectionalMTV(currentClearance, obb, dir);
                if (mtv > combined) combined = mtv;
              }
              if (combined > 0) {
                const newOffset = currentOffset + sign * (combined + SNAP_GAP);
                if (Math.abs(newOffset) <= slideRange) {
                  slideCandidates.push({
                    newOffset,
                    slideMag: combined + SNAP_GAP,
                    label,
                  });
                  debugLog.push(
                    `cb-slide ${label}: combined=${(combined * 1000).toFixed(0)}mm → offset=${(newOffset * 1000).toFixed(0)}mm (range=±${(slideRange * 1000).toFixed(0)}mm)`
                  );
                } else {
                  debugLog.push(
                    `cb-slide ${label}: combined=${(combined * 1000).toFixed(0)}mm → offset=${(newOffset * 1000).toFixed(0)}mm exceeds range ±${(slideRange * 1000).toFixed(0)}mm`
                  );
                }
              }
            }
            slideCandidates.sort((a, b) => a.slideMag - b.slideMag);

            for (const { newOffset, slideMag, label } of slideCandidates) {
              const validation = checkPlacement(
                { ...object, clearanceOffset: newOffset },
                roomData,
                options
              );
              if (validation.valid) {
                debugLog.push(
                  `clearance-boundary: clearance-slide offset=${(newOffset * 1000).toFixed(0)}mm ${label} → valid`
                );
                return {
                  snapped: true,
                  center: object.center,
                  clearanceOffset: newOffset,
                  snapDistance: slideMag,
                };
              }
              debugLog.push(
                `cb-slide ${label}: offset=${(newOffset * 1000).toFixed(0)}mm → still invalid (${validation.collisions?.[0]?.type})`
              );
            }
          }
        }

        // Step 2: MTV-based fixture push along ±parentWallDir.
        // For multi-footprint fixtures (cabinets) the binding push is the worst
        // (largest) MTV across all footprints — every footprint must clear
        // every blocking wall.
        const objFootprints = getObjectClearanceFootprints(
          object,
          getClearanceConfig(object),
          roomData.objects || []
        );
        const pushCandidates = [];
        for (const sign of [+1, -1]) {
          const dir = { x: sign * wallDir.x, z: sign * wallDir.z };
          const label = sign > 0 ? '+wallDir' : '-wallDir';
          let combined = 0;
          let perWallLog = [];
          for (const { wallId, obb } of blockingObbs) {
            let mtv = 0;
            for (const fp of objFootprints) {
              if (!fp || fp.length < 4) continue;
              const m = computeDirectionalMTV(fp, obb, dir);
              if (m > mtv) mtv = m;
            }
            perWallLog.push(`${wallId.slice(-6)}=${(mtv * 1000).toFixed(0)}mm`);
            if (mtv > combined) combined = mtv;
          }
          debugLog.push(
            `cb-push ${label}: ${perWallLog.join(', ')} → combined=${(combined * 1000).toFixed(0)}mm`
          );
          if (combined > 0) {
            pushCandidates.push({
              dir,
              label,
              shift: combined + SNAP_GAP,
            });
          }
        }
        pushCandidates.sort((a, b) => a.shift - b.shift);

        for (const { dir, label, shift } of pushCandidates) {
          if (shift > MAX_SNAP_DISTANCE) {
            debugLog.push(
              `cb-push ${label}: ${(shift * 1000).toFixed(0)}mm > max ${(MAX_SNAP_DISTANCE * 1000).toFixed(0)}mm`
            );
            continue;
          }
          const snappedCenter = {
            x: object.center.x + shift * dir.x,
            y: object.center.y,
            z: object.center.z + shift * dir.z,
          };
          const validation = checkPlacement(
            { ...object, center: snappedCenter },
            roomData,
            options
          );
          if (validation.valid) {
            debugLog.push(
              `clearance-boundary: shift ${(shift * 1000).toFixed(0)}mm ${label} → valid`
            );
            return {
              snapped: true,
              center: snappedCenter,
              snapDistance: shift,
            };
          }
          debugLog.push(
            `cb-push ${label}: ${(shift * 1000).toFixed(0)}mm → still invalid (${validation.collisions?.[0]?.type})`
          );
        }

        if (pushCandidates.length === 0) {
          debugLog.push(
            `clearance-boundary: no positive MTV in either direction — clearance may exit perpendicular to parent wall`
          );
        }
      }
    }
  }

  // --- Handle fixture-to-fixture and clearance collisions ---
  const fixtureCollisions = collisionResult.collisions.filter(
    (c) =>
      c.type === 'fixture-to-fixture' ||
      c.type === 'fixture-clearance' ||
      c.type === 'clearance-blocked'
  );

  if (fixtureCollisions.length === 0) return null;

  // For tubs and showers, the clearance zone can slide along the physical
  // body. Try repositioning the clearance before moving the whole fixture.
  // Only fires when there are no physical-body collisions — sliding the
  // clearance can't resolve a body-vs-body overlap.
  {
    const SLIDEABLE_TYPES = new Set(['bathtub', 'shower']);
    const objType = (object.type || '').toLowerCase();
    const hasClearanceCollision = fixtureCollisions.some(
      (c) => c.type === 'clearance-blocked'
    );
    const hasPhysicalCollision = fixtureCollisions.some(
      (c) => c.type === 'fixture-to-fixture'
    );
    if (
      SLIDEABLE_TYPES.has(objType) &&
      hasClearanceCollision &&
      !hasPhysicalCollision &&
      parentWall
    ) {
      const wallInfo = getWallAxis(parentWall);
      if (wallInfo) {
        const { wallDir } = wallInfo;
        const objConfig = getClearanceConfig(object);
        const clearanceW = objConfig.clearanceWidth || object.dimensions.x;
        const physicalW = object.dimensions.x;
        const slideRange = (physicalW - clearanceW) / 2;

        if (slideRange > 0.01) {
          const currentClearance = getObjectClearanceCorners(
            object,
            objConfig,
            object.clearanceOffset || 0
          );
          const currentOffset = object.clearanceOffset || 0;
          let bestOffset = null;

          for (const collision of fixtureCollisions) {
            if (collision.type !== 'clearance-blocked') continue;
            const target = (roomData.objects || []).find(
              (o) => o.id === collision.target.id
            );
            if (!target) continue;
            const targetCorners = getObjectCorners(target);
            const leftSlide = computeDirectionalMTV(
              currentClearance,
              targetCorners,
              { x: -wallDir.x, z: -wallDir.z }
            );
            const rightSlide = computeDirectionalMTV(
              currentClearance,
              targetCorners,
              wallDir
            );
            const candidates = [];
            if (leftSlide > 0) {
              const offset = currentOffset - (leftSlide + SNAP_GAP);
              if (Math.abs(offset) <= slideRange) candidates.push(offset);
            }
            if (rightSlide > 0) {
              const offset = currentOffset + (rightSlide + SNAP_GAP);
              if (Math.abs(offset) <= slideRange) candidates.push(offset);
            }
            for (const offset of candidates) {
              if (
                bestOffset === null ||
                Math.abs(offset) < Math.abs(bestOffset)
              ) {
                // Verify this offset clears every clearance-blocked collision.
                const testCorners = getObjectClearanceCorners(
                  object,
                  objConfig,
                  offset
                );
                let valid = true;
                for (const c2 of fixtureCollisions) {
                  if (c2.type !== 'clearance-blocked') continue;
                  const t2 = (roomData.objects || []).find(
                    (o) => o.id === c2.target.id
                  );
                  if (
                    t2 &&
                    doPolygonsOverlap(testCorners, getObjectCorners(t2))
                  ) {
                    valid = false;
                    break;
                  }
                }
                if (valid) bestOffset = offset;
              }
            }
          }

          if (bestOffset !== null) {
            debugLog.push(
              `clearance-slide: offset=${(bestOffset * 1000).toFixed(0)}mm (range ±${(slideRange * 1000).toFixed(0)}mm)`
            );
            return {
              snapped: true,
              center: object.center,
              snapDistance: 0,
              clearanceOffset: bestOffset,
            };
          }
          debugLog.push(
            `clearance-slide: no valid offset within range ±${(slideRange * 1000).toFixed(0)}mm`
          );
        }
      }
    }
  }

  // Determine snap axis — use wall direction if wall-mounted
  let snapAxis;
  if (parentWall) {
    const wallInfo = getWallAxis(parentWall);
    if (!wallInfo) return null;
    snapAxis = wallInfo.wallDir;
  } else {
    const target = (roomData.objects || []).find(
      (o) => o.id === fixtureCollisions[0].target.id
    );
    if (!target?.center) return null;
    const dx = object.center.x - target.center.x;
    const dz = object.center.z - target.center.z;
    snapAxis = Math.abs(dx) >= Math.abs(dz) ? { x: 1, z: 0 } : { x: 0, z: 1 };
  }

  const objConfig = getClearanceConfig(object);
  const objCorners = getObjectCorners(object);

  // For clearance collisions, try resolving with minimum clearance push first,
  // then fall back to physical push if needed.
  // This keeps fixtures as close as possible — only moving enough to clear
  // the clearance zone, not the full physical body.
  let bestSnap = null;

  // Try both directions along snapAxis. The previous one-direction-only
  // logic (via approachFromLeft heuristic) was correct for same-wall
  // collisions but broke for cross-wall collisions: when the target is on
  // a perpendicular wall, projecting its center onto the dragged object's
  // wallDir collapses meaningful 2D position to a meaningless 1D scalar.
  // The wrong direction produces a "valid but huge" MTV (push through and
  // out the far side) that exceeds MAX_SNAP_DISTANCE → spurious rejection.
  // Mirroring the door-swing handler's pattern: compute both, prefer smaller.
  const PUSH_DIRS = [
    { dir: { x: snapAxis.x, z: snapAxis.z }, label: 'right' },
    { dir: { x: -snapAxis.x, z: -snapAxis.z }, label: 'left' },
  ];

  for (const collision of fixtureCollisions) {
    const target = (roomData.objects || []).find(
      (o) => o.id === collision.target.id
    );
    if (!target?.center || !target?.dimensions) continue;
    const targetConfig = getClearanceConfig(target);

    // Build candidate shifts per direction.
    const candidates = [];
    for (const { dir: pushDir, label } of PUSH_DIRS) {
      // Step 1: clearance-only resolution.
      // For multi-footprint fixtures (cabinets), the binding push is the
      // worst MTV across all relevant footprints — every footprint must
      // clear the obstacle.
      let clearanceShift = 0;
      if (
        collision.type === 'clearance-blocked' ||
        collision.type === 'fixture-clearance'
      ) {
        if (collision.type === 'clearance-blocked') {
          // Push each of MY footprints out of target's body.
          const myFootprints = getObjectClearanceFootprints(
            object,
            objConfig,
            roomData.objects || []
          );
          const targetBody = getObjectCorners(target);
          for (const fp of myFootprints) {
            if (!fp || fp.length < 4) continue;
            const mtv = computeDirectionalMTV(fp, targetBody, pushDir);
            if (mtv > clearanceShift) clearanceShift = mtv;
          }
        } else {
          // fixture-clearance: my body is inside target's clearance footprints.
          // Push my body until it's clear of every target footprint.
          const targetFootprints = getObjectClearanceFootprints(
            target,
            targetConfig,
            roomData.objects || []
          );
          for (const fp of targetFootprints) {
            if (!fp || fp.length < 4) continue;
            const mtv = computeDirectionalMTV(objCorners, fp, pushDir);
            if (mtv > clearanceShift) clearanceShift = mtv;
          }
        }
        debugLog.push(
          `${collision.type}: clearance MTV=${(clearanceShift * 1000).toFixed(0)}mm vs ${target.type} (push ${label})`
        );
      }

      // Step 2: if clearance push isn't enough, add physical push
      let totalShift = clearanceShift > 0 ? clearanceShift + SNAP_GAP : 0;
      if (collision.type === 'fixture-to-fixture' || totalShift === 0) {
        const physicalShift = computeDirectionalMTV(
          objCorners,
          getObjectCorners(target),
          pushDir
        );
        debugLog.push(
          `${collision.type}: physical MTV=${(physicalShift * 1000).toFixed(0)}mm vs ${target.type} (push ${label})`
        );
        if (physicalShift > 0) {
          totalShift = physicalShift + SNAP_GAP;
        }
      }

      if (totalShift === 0) continue;
      if (totalShift > MAX_SNAP_DISTANCE) {
        debugLog.push(
          `${collision.type}: ${(totalShift * 1000).toFixed(0)}mm > max vs ${target.type} ${target.id?.slice(-8)} (push ${label})`
        );
        continue;
      }
      candidates.push({ pushDir, label, totalShift });
    }

    // Try smallest candidate first; first one that revalidates wins for
    // this collision. bestSnap tracks the smallest valid shift across
    // all collisions overall.
    candidates.sort((a, b) => a.totalShift - b.totalShift);
    for (const { pushDir, label, totalShift } of candidates) {
      const snappedCenter = {
        x: object.center.x + totalShift * pushDir.x,
        y: object.center.y,
        z: object.center.z + totalShift * pushDir.z,
      };
      const validation = checkPlacement(
        { ...object, center: snappedCenter },
        roomData,
        options
      );
      if (validation.valid) {
        if (!bestSnap || totalShift < bestSnap.snapDistance) {
          bestSnap = {
            snapped: true,
            center: snappedCenter,
            snapDistance: totalShift,
          };
          debugLog.push(
            `${collision.type}: ${(totalShift * 1000).toFixed(0)}mm vs ${target.type} (push ${label}) → valid`
          );
        }
        break; // first valid for this collision; smaller candidates already tried
      } else {
        debugLog.push(
          `${collision.type}: ${(totalShift * 1000).toFixed(0)}mm vs ${target.type} (push ${label}) → still invalid (${validation.collisions?.[0]?.type})`
        );
      }
    }
  }

  return bestSnap;
}

module.exports = { snapToValidPosition, MAX_SNAP_DISTANCE };
